import { useState } from 'react';
import { openSunmiDrawer } from '../../../utils/sunmiPrinter';

interface NoSaleModalProps {
  onClose: () => void;
  onConfirm: (reason: string, note: string) => void;
  userRole: 'cashier' | 'supervisor' | 'manager';
}

export default function NoSaleModal({ onClose, onConfirm, userRole }: NoSaleModalProps) {
  const [selectedReason, setSelectedReason] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const reasons = [
    'Change Required',
    'Drawer Check',
    'Bill Break',
    'Manager Request',
    'System Test',
    'Other',
  ];

  const handleConfirm = () => {
    if (!selectedReason) {
      setError('Please select a reason');
      return;
    }

    if (userRole === 'cashier') {
      setError('Cashier role cannot perform No Sale. Supervisor approval required.');
      return;
    }

    openSunmiDrawer();
    onConfirm(selectedReason, note);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] min-h-[300px] overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <i className="ri-safe-2-line text-xl text-gray-700"></i>
            <h2 className="text-lg font-semibold text-gray-900">No Sale - Open Drawer</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {userRole === 'cashier' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 text-lg mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-red-800">Access Restricted</p>
                <p className="text-xs text-red-600 mt-1">Cashiers cannot perform No Sale operations. Please request supervisor assistance.</p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {reasons.map((reason) => (
                <label
                  key={reason}
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="radio"
                    name="reason"
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={(e) => {
                      setSelectedReason(e.target.value);
                      setError('');
                    }}
                    className="w-4 h-4 text-teal-600 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">{reason}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Note (Optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter any additional details..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-sm"
              rows={3}
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">{note.length}/200 characters</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500"></i>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={userRole === 'cashier'}
            className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            Open Drawer
          </button>
        </div>
      </div>
    </div>
  );
}
